/**
 * Mobile composer D-cluster wireframes: queue while streaming, attach sheet, pilot feedback.
 * Access: /?test=mobile-composer-d
 *
 * Bets:
 * - docs/product/bets/2026-07-24-mobile-chat-queue-message-while-streaming.md
 * - docs/product/bets/2026-07-24-mobile-chat-file-and-camera-attachments.md
 * - docs/product/bets/2026-07-24-mobile-pilot-feedback-trigger.md
 *
 * Prototype QA (2026-07-24):
 * - Queue: web-like strip; tap row to edit (pull into composer); editing pauses auto-send
 * - Feedback: category + description + optional 1-5 rating; no expected/actual
 * - Phone chrome is illustrative; Expo must use ThemeContext / sheet tokens
 */

import { useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import {
  ArrowUp,
  Camera,
  ChevronDown,
  ChevronRight,
  FileText,
  Image as ImageIcon,
  Link2,
  Loader2,
  MessageSquare,
  Paperclip,
  Square,
  X,
} from 'lucide-react'

type Tab = 'queue' | 'attach' | 'feedback'
type FeedbackCat = 'bug' | 'feedback' | 'agent-issue' | 'question'

const TABS: { id: Tab; label: string }[] = [
  { id: 'queue', label: 'Queue while streaming' },
  { id: 'attach', label: 'Attach (+ sheet)' },
  { id: 'feedback', label: 'Pilot feedback' },
]

const FEEDBACK_CATS: { id: FeedbackCat; label: string }[] = [
  { id: 'bug', label: 'Bug' },
  { id: 'feedback', label: 'Feedback' },
  { id: 'agent-issue', label: 'Agent issue' },
  { id: 'question', label: 'Question' },
]

function PhoneFrame({ children, label }: { children: React.ReactNode; label: string }) {
  return (
    <div className="mx-auto w-full max-w-[390px]">
      <p className="text-xs text-muted-foreground mb-2">{label}</p>
      <div className="rounded-[28px] border border-border bg-stone-900 text-stone-100 shadow-xl overflow-hidden min-h-[640px] flex flex-col">
        <div className="h-11 flex items-center justify-center text-[11px] text-stone-400 border-b border-stone-800">
          9:41 · Maater
        </div>
        {children}
      </div>
    </div>
  )
}

function QueueScene() {
  const [queue, setQueue] = useState<string[]>([
    'Also check domain Insulation after this turn.',
  ])
  const [draft, setDraft] = useState('')
  const [streaming, setStreaming] = useState(true)
  /** When true, stream-end must NOT auto-flush (user is editing a pulled-back item). */
  const [editPaused, setEditPaused] = useState(false)
  const [queueExpanded, setQueueExpanded] = useState(true)
  const [lastAction, setLastAction] = useState<string | null>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const enqueueDraft = () => {
    const v = draft.trim()
    if (!v) return
    setQueue((prev) => [...prev, v])
    setDraft('')
    setEditPaused(false)
    setLastAction('Queued follow-up')
  }

  const pullToEdit = (index: number) => {
    const msg = queue[index]
    if (msg == null) return
    setQueue((prev) => prev.filter((_, i) => i !== index))
    setDraft(msg)
    setEditPaused(true)
    setLastAction('Editing queued message - auto-send paused')
    requestAnimationFrame(() => inputRef.current?.focus())
  }

  const cancelAt = (index: number) => {
    setQueue((prev) => prev.filter((_, i) => i !== index))
    setLastAction('Cancelled queued message')
  }

  const onStreamEnded = () => {
    setStreaming(false)
    if (editPaused) {
      setLastAction('Stream ended - edit pause held; draft stays in composer')
      return
    }
    if (queue.length === 0) {
      setLastAction('Stream ended - nothing queued')
      return
    }
    const [next, ...rest] = queue
    setQueue(rest)
    setLastAction(`Auto-sent: "${next}"`)
  }

  const onStop = () => {
    setStreaming(false)
    if (queue.length === 0) {
      setLastAction('Stopped - nothing queued')
      return
    }
    // Stop still flushes (web parity); edit-pause only blocks silent auto-send on natural end.
    const [next, ...rest] = queue
    setQueue(rest)
    setEditPaused(false)
    setLastAction(`Stop flushed: "${next}"`)
  }

  const hasDraft = draft.trim().length > 0
  /** Cursor pattern: Stop lives above composer; primary slot is Send/Queue when draft has text. */
  const showComposerSend = !streaming || hasDraft

  return (
    <div className="space-y-4">
      <PhoneFrame label="Cursor-style: Stop above composer · Send/Queue in primary slot when drafting">
        <div className="flex-1 p-4 space-y-3 overflow-y-auto">
          <p className="text-xs text-stone-500 uppercase tracking-wide">Chat</p>
          <div className="rounded-lg bg-stone-800/80 px-3 py-2 text-sm text-stone-200">
            Looking up HPA disclosure rules…
          </div>
          {streaming ? (
            <div className="flex items-center gap-2 text-xs text-amber-200/90 italic">
              <span className="inline-flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400/70 animate-pulse" />
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400/40 animate-pulse" />
              </span>
              Reading 4 documents
            </div>
          ) : (
            <p className="text-xs text-stone-500">Stream idle</p>
          )}
        </div>

        <div className="border-t border-stone-800 bg-stone-950">
          {/* Streaming control bar (Cursor: Stop above the prompt, separate from Send) */}
          {streaming ? (
            <div className="flex items-center gap-2 px-3 py-2 border-b border-stone-800 bg-stone-900/90">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-stone-400 shrink-0" />
              <span className="text-xs text-stone-400 flex-1 truncate">
                Agent responding…
              </span>
              <button
                type="button"
                onClick={onStop}
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-stone-100 bg-stone-800 hover:bg-stone-700 border border-stone-600"
                title="Stop current stream (then flushes next queued)"
              >
                <Square className="h-3 w-3 fill-current" />
                Stop
              </button>
            </div>
          ) : null}

          {queue.length > 0 ? (
            <div className="border-b border-stone-800/80 bg-stone-900/80">
              <button
                type="button"
                onClick={() => setQueueExpanded((v) => !v)}
                className="w-full flex items-center gap-1.5 px-3 py-2 text-xs text-stone-400 hover:text-stone-200"
              >
                {queueExpanded ? (
                  <ChevronDown className="w-3.5 h-3.5 shrink-0" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                )}
                <span className="font-medium text-stone-200">{queue.length} Queued</span>
                {streaming && !editPaused ? (
                  <span className="ml-auto text-stone-500">Sends after this reply</span>
                ) : null}
                {editPaused ? (
                  <span className="ml-auto text-amber-300/90">Auto-send paused (editing)</span>
                ) : null}
              </button>
              {queueExpanded ? (
                <div className="divide-y divide-stone-800">
                  {queue.map((msg, i) => (
                    <div key={`${i}-${msg.slice(0, 12)}`} className="flex items-start gap-2 px-3 py-2">
                      <button
                        type="button"
                        className="flex-1 min-w-0 text-left"
                        onClick={() => pullToEdit(i)}
                      >
                        <p className="text-xs text-stone-100 truncate">{msg}</p>
                        <p className="text-[10px] text-stone-500 mt-0.5">
                          Tap to edit · pulls into composer
                        </p>
                      </button>
                      <button
                        type="button"
                        onClick={() => cancelAt(i)}
                        className="p-1 rounded hover:bg-stone-800 text-stone-400"
                        aria-label="Cancel queued message"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="p-3 space-y-2">
            <div className="flex gap-2 items-end">
              <textarea
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="flex-1 min-h-[44px] max-h-24 rounded-lg bg-stone-900 border border-stone-700 px-3 py-2 text-sm text-stone-100 placeholder:text-stone-500 focus:outline-none focus:border-sky-500"
                placeholder={
                  streaming
                    ? editPaused
                      ? 'Edit follow-up, then Send to re-queue…'
                      : 'Add a follow-up…'
                    : 'Message'
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    if (streaming && hasDraft) enqueueDraft()
                    else if (!streaming && hasDraft) {
                      setLastAction(`Sent: "${draft.trim()}"`)
                      setDraft('')
                      setEditPaused(false)
                      setStreaming(true)
                    }
                  }
                }}
              />
              {showComposerSend ? (
                <Button
                  size="icon"
                  className="h-10 w-10 shrink-0"
                  disabled={!hasDraft}
                  onClick={() => {
                    if (streaming) {
                      enqueueDraft()
                      return
                    }
                    setLastAction(`Sent: "${draft.trim()}"`)
                    setDraft('')
                    setEditPaused(false)
                    setStreaming(true)
                  }}
                  title={streaming ? (editPaused ? 'Re-queue edited message' : 'Queue follow-up') : 'Send'}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
              ) : (
                <div
                  className="h-10 w-10 shrink-0 rounded-md border border-stone-800 bg-stone-900/50 flex items-center justify-center"
                  title="Type to queue a follow-up · Stop is in the bar above"
                  aria-hidden
                >
                  <Square className="h-3.5 w-3.5 text-stone-600" />
                </div>
              )}
            </div>
            {editPaused ? (
              <p className="text-[10px] text-amber-300/90">
                Editing - auto-send paused. Tap Send (↑) to re-queue, or leave draft. Stop is in the
                bar above.
              </p>
            ) : streaming ? (
              <p className="text-[10px] text-stone-500">
                Stop above · Send queues when draft has text · empty composer shows muted stop cue
                only
              </p>
            ) : (
              <p className="text-[10px] text-stone-500">
                Idle · Send starts a turn · queue strip matches web
              </p>
            )}
          </div>
        </div>
      </PhoneFrame>

      {/* Controls on page chrome (not inside phone) so contrast stays readable */}
      <div className="max-w-[390px] mx-auto space-y-2">
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setStreaming(true)
              setLastAction('Simulating stream…')
            }}
          >
            Simulate stream
          </Button>
          <Button size="sm" variant="secondary" onClick={onStreamEnded}>
            Stream ended (auto-send)
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setDraft('testing the next queue')
              setEditPaused(false)
            }}
          >
            Fill draft
          </Button>
          <Button size="sm" variant="outline" disabled={!streaming || !draft.trim()} onClick={enqueueDraft}>
            Queue draft
          </Button>
        </div>
        {lastAction ? (
          <p className="text-xs text-muted-foreground border rounded-md px-3 py-2 bg-muted/40">
            {lastAction}
          </p>
        ) : null}
      </div>
    </div>
  )
}

function AttachScene() {
  const [sheetOpen, setSheetOpen] = useState(true)
  const [chip, setChip] = useState<string | null>('exam-page.jpg')

  return (
    <PhoneFrame label="+ sheet: Link · Camera · Library · File">
      <div className="flex-1 p-4">
        <p className="text-sm text-stone-400">Composer with attachment chip</p>
      </div>
      <div className="border-t border-stone-800 p-3 space-y-2 bg-stone-950 relative">
        {chip ? (
          <div className="flex items-center gap-2 rounded-md border border-stone-700 bg-stone-900 px-2 py-1.5 text-xs">
            <ImageIcon className="h-3.5 w-3.5 text-stone-400" />
            <span className="truncate flex-1 text-stone-200">{chip}</span>
            <button type="button" onClick={() => setChip(null)} aria-label="Remove">
              <X className="h-3.5 w-3.5 text-stone-400" />
            </button>
          </div>
        ) : null}
        <div className="flex gap-2 items-center">
          <Button
            size="icon"
            variant="outline"
            className="h-10 w-10 border-stone-600 bg-stone-900 text-stone-100 hover:bg-stone-800"
            onClick={() => setSheetOpen(true)}
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <div className="flex-1 h-10 rounded-lg border border-stone-700 bg-stone-900 px-3 flex items-center text-sm text-stone-500">
            What does this page require?
          </div>
        </div>

        {sheetOpen ? (
          <div className="absolute inset-x-0 bottom-0 rounded-t-2xl border border-stone-700 bg-stone-900 p-4 shadow-2xl">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-sm">Attach</p>
              <button type="button" onClick={() => setSheetOpen(false)}>
                <X className="h-4 w-4 text-stone-400" />
              </button>
            </div>
            <div className="space-y-1">
              {[
                { icon: Link2, label: 'Link document', action: () => setSheetOpen(false) },
                {
                  icon: Camera,
                  label: 'Camera',
                  action: () => {
                    setChip('camera-capture.jpg')
                    setSheetOpen(false)
                  },
                },
                {
                  icon: ImageIcon,
                  label: 'Photo library',
                  action: () => {
                    setChip('screenshot.png')
                    setSheetOpen(false)
                  },
                },
                {
                  icon: FileText,
                  label: 'File',
                  action: () => {
                    setChip('notes.pdf')
                    setSheetOpen(false)
                  },
                },
              ].map((row) => (
                <button
                  key={row.label}
                  type="button"
                  onClick={row.action}
                  className="w-full flex items-center gap-3 rounded-lg px-3 py-3 text-left text-sm hover:bg-stone-800"
                >
                  <row.icon className="h-4 w-4 text-stone-400" />
                  {row.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </PhoneFrame>
  )
}

function FeedbackScene() {
  const [category, setCategory] = useState<FeedbackCat>('feedback')
  const [text, setText] = useState('')
  const [rating, setRating] = useState<number | null>(null)
  const [done, setDone] = useState(false)

  return (
    <PhoneFrame label="Chat … → Send feedback (+ optional 1-5 rating; no expected/actual)">
      <div className="flex-1 bg-black/50 flex items-end p-3">
        <div className="w-full rounded-2xl bg-background text-foreground border border-border p-4 space-y-3 max-h-[85%] overflow-y-auto">
          <div className="flex items-start gap-2">
            <MessageSquare className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div>
              <p className="font-semibold">Send feedback</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Files to Maater Product / pilot-feedback. Same API as web.
              </p>
            </div>
          </div>

          {done ? (
            <div className="space-y-3">
              <p className="text-sm font-medium">Thanks - feedback sent.</p>
              <Button
                size="sm"
                className="w-full"
                onClick={() => {
                  setDone(false)
                  setText('')
                  setRating(null)
                }}
              >
                Close
              </Button>
            </div>
          ) : (
            <>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Category
              </p>
              <div className="flex flex-wrap gap-1.5">
                {FEEDBACK_CATS.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCategory(c.id)}
                    className={cn(
                      'text-xs px-2 py-1 rounded-md border',
                      category === c.id
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border text-foreground',
                    )}
                  >
                    {c.label}
                  </button>
                ))}
              </div>

              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                What happened
              </p>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Describe the issue or idea…"
                className="min-h-[100px] text-sm"
              />

              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Rating (optional)
              </p>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Button
                    key={n}
                    type="button"
                    size="sm"
                    variant={rating === n ? 'default' : 'outline'}
                    className="min-w-9 px-2"
                    onClick={() => setRating(rating === n ? null : n)}
                    aria-label={`Rating ${n}`}
                  >
                    {n}
                  </Button>
                ))}
              </div>

              <p className="text-[10px] text-muted-foreground">
                Diagnostics: conversation id · surface: mobile · no expected/actual fields (by
                design) · screenshot NTH after attachments
              </p>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost">
                  Cancel
                </Button>
                <Button size="sm" disabled={!text.trim()} onClick={() => setDone(true)}>
                  Send
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </PhoneFrame>
  )
}

export function MobileComposerDWireframes() {
  const [tab, setTab] = useState<Tab>('queue')

  return (
    <div
      className="min-h-screen bg-background text-foreground p-6"
      data-testid="mobile-composer-d-wireframe"
    >
      <header className="max-w-5xl mx-auto mb-6 space-y-2">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">Wireframe cluster</p>
        <h1 className="text-2xl font-semibold">Mobile composer D bets</h1>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Queue (web-parity strip + edit/pause) · Attach sheet · Pilot feedback (rating, no
          expected/actual). Phone colors are mock only - Expo should use ThemeContext / sheet
          tokens.
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          {TABS.map((t) => (
            <Button
              key={t.id}
              size="sm"
              variant={tab === t.id ? 'default' : 'outline'}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </Button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline">queue → edit + pause locked</Badge>
          <Badge variant="outline">feedback → + rating</Badge>
          <Badge variant="secondary">attachments → human-only</Badge>
        </div>
      </header>

      <div className="max-w-5xl mx-auto">
        {tab === 'queue' ? <QueueScene /> : null}
        {tab === 'attach' ? <AttachScene /> : null}
        {tab === 'feedback' ? <FeedbackScene /> : null}
      </div>
    </div>
  )
}
